// One renderer, one scene (`src/world/scene.ts`), a subtree per side.
//
// The Atlas used to build its own WebGLRenderer and its own canvas inside the
// menu pane, which is a second GL context, a second `renderer.info` and a
// second resolution governor for the same machine. Instead it draws through the
// sim's renderer into the rect of a transparent element in the pane, scissored
// so nothing outside that rect is touched. This module owns that contract: who
// the shared renderer is, where the rect is, and everything the sim has to get
// back when the Atlas hands the renderer over.

import * as THREE from 'three';
import { TEST_SWITCHES } from '../urlFlags.ts';

/** The stage's rect in CLIENT coordinates, the way getBoundingClientRect gives it. */
export interface StageRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StageConfig {
  /** Set inside begin(), restored in end(), so the sim keeps its own. */
  clearColor?: THREE.ColorRepresentation;
  toneMappingExposure?: number;
  /** The Atlas's cascaded shadow maps (docs/roadmap/05-atmosphere-and-light.md,
   *  "Shadows"). The sim casts none anywhere, so its own values are always the
   *  three.js defaults - saved and given back the same way as the two above. */
  shadowMap?: {
    enabled: boolean;
    type: THREE.ShadowMapType;
    autoUpdate: boolean;
  };
}

export interface StageHandle {
  /** Null when the stage is not drawable: unmounted, hidden, or zero-sized. */
  rect(): StageRect | null;
  /** Viewport + scissor the shared canvas down to the stage. False when not drawable. */
  begin(): boolean;
  /** Hand the renderer back: full viewport, no scissor, default render target. */
  end(): void;
  /** Give the sim its drawing buffer back. No-op unless the stage changed it. */
  release(): void;
  setPixelRatio(px: number): void;
  /** What begin() applies from here on. The Atlas eases its own per band. */
  setToneMappingExposure(v: number): void;
  setFrame(fn: () => void): void;
  /** Run the frame callback the stage's owner installed. */
  frame(): void;
  dispose(): void;
}

// ---------- the renderer handoff ----------
//
// main.ts publishes its renderer here at boot and the Atlas reads it at mount.
// A direct import of main.ts would be circular, and a global would be a hook
// the instruments could confuse for an app-side one.

let shared: THREE.WebGLRenderer | null = null;

export function setSharedRenderer(renderer: THREE.WebGLRenderer | null): void {
  shared = renderer;
}

export function sharedRenderer(): THREE.WebGLRenderer {
  if (!shared) throw new Error('no shared renderer: setSharedRenderer() has not run');
  return shared;
}

let active: StageHandle | null = null;

/** The stage currently mounted in the menu pane, if any. */
export function activeStage(): StageHandle | null {
  return active;
}

const scratch = new THREE.Vector2();
const savedClear = new THREE.Color();
// `?stagereallocindraw=1` applies a new pixel ratio inside the draw bracket again, as it did.
const REALLOC_IN_DRAW = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).get('stagereallocindraw') === '1';

export function createStage(
  renderer: THREE.WebGLRenderer,
  pane: HTMLElement,
  config: StageConfig = {},
): StageHandle {
  const canvas = renderer.domElement;
  const basePixelRatio = renderer.getPixelRatio();
  let stagePixelRatio = basePixelRatio;
  let frameFn: (() => void) | null = null;
  let engaged = false;
  let savedExposure = renderer.toneMappingExposure;
  let savedAlpha = 1;
  let savedShadowEnabled = renderer.shadowMap.enabled;
  let savedShadowType = renderer.shadowMap.type;
  let savedShadowAutoUpdate = renderer.shadowMap.autoUpdate;
  let disposed = false;

  function rect(): StageRect | null {
    if (disposed || !pane.isConnected) return null;
    const r = pane.getBoundingClientRect();
    if (r.width < 1 || r.height < 1) return null;
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }

  function applyPixelRatio(px: number): void {
    if (Math.abs(renderer.getPixelRatio() - px) < 1e-4) return;
    // setPixelRatio reallocates the drawing buffer, so this is guarded rather
    // than called per frame.
    renderer.setPixelRatio(px);
  }

  function begin(): boolean {
    const r = rect();
    if (!r) return false;
    applyPixelRatio(stagePixelRatio);
    const cr = canvas.getBoundingClientRect();
    // setViewport/setScissor take logical pixels with y measured up from the
    // bottom of the canvas; getBoundingClientRect measures down from its top.
    const x = r.x - cr.left;
    const y = cr.height - (r.y - cr.top) - r.height;
    renderer.setViewport(x, y, r.width, r.height);
    renderer.setScissor(x, y, r.width, r.height);
    renderer.setScissorTest(true);
    savedExposure = renderer.toneMappingExposure;
    renderer.getClearColor(savedClear);
    savedAlpha = renderer.getClearAlpha();
    if (config.clearColor !== undefined) renderer.setClearColor(config.clearColor, 1);
    if (config.toneMappingExposure !== undefined) {
      renderer.toneMappingExposure = config.toneMappingExposure;
    }
    savedShadowEnabled = renderer.shadowMap.enabled;
    savedShadowType = renderer.shadowMap.type;
    savedShadowAutoUpdate = renderer.shadowMap.autoUpdate;
    if (config.shadowMap) {
      renderer.shadowMap.enabled = config.shadowMap.enabled;
      renderer.shadowMap.type = config.shadowMap.type;
      renderer.shadowMap.autoUpdate = config.shadowMap.autoUpdate;
    }
    engaged = true;
    return true;
  }

  function end(): void {
    if (!engaged) return;
    engaged = false;
    renderer.setScissorTest(false);
    renderer.setRenderTarget(null);
    renderer.getSize(scratch);
    renderer.setViewport(0, 0, scratch.x, scratch.y);
    renderer.setScissor(0, 0, scratch.x, scratch.y);
    renderer.setClearColor(savedClear, savedAlpha);
    renderer.toneMappingExposure = savedExposure;
    renderer.shadowMap.enabled = savedShadowEnabled;
    renderer.shadowMap.type = savedShadowType;
    renderer.shadowMap.autoUpdate = savedShadowAutoUpdate;
  }

  const handle: StageHandle = {
    rect,
    begin,
    end,
    release(): void {
      end();
      applyPixelRatio(basePixelRatio);
    },
    setPixelRatio(px: number): void {
      stagePixelRatio = px;
      // Reallocating inside the draw bracket clears the frame drawn to black; begin() applies it next frame.
      if (!disposed && rect() && (!engaged || REALLOC_IN_DRAW)) applyPixelRatio(px);
    },
    setToneMappingExposure(v: number): void {
      config.toneMappingExposure = v;
      // Mid-visit: the value is applied in begin(), so a change between begin()
      // and end() has to reach the renderer here or it lands a frame late.
      if (engaged) renderer.toneMappingExposure = v;
    },
    setFrame(fn: () => void): void {
      frameFn = fn;
    },
    frame(): void {
      frameFn?.();
    },
    dispose(): void {
      if (disposed) return;
      end();
      applyPixelRatio(basePixelRatio);
      disposed = true;
      frameFn = null;
      if (active === handle) active = null;
    },
  };
  active?.dispose();
  active = handle;
  return handle;
}
